import React, { useState, useEffect, useCallback } from 'react';
import { fetchGLAccountTransactions } from '../api';
import { formatCurrency } from '../utils/currency';
import { usePagination } from '../hooks/usePagination';
import PaginationBar from './shared/PaginationBar';

// "View Transactions" drill-down for a single Chart of Accounts row — the
// transaction-level detail behind the balance ChartOfAccounts.js shows, so a
// discrepancy (e.g. on a control account like 2300 Trust Liability) can be
// investigated without database access. Wide right-slide-over, matching
// InvoiceDetails.js's pattern for data-dense detail views (this table is
// the same shape: date/entry/description/source/debit/credit/running
// balance), rather than the smaller centered shared/Modal.js.
//
// `account` only needs { id, code, name } — kept generic (not a full COA
// row) so this panel can be reused from TrialBalanceReport.js later without
// rework.
export default function GLAccountTransactionsPanel({ account, authAxios, showFormMessage, onClose }) {
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState(null);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const load = useCallback(async () => {
    if (!account?.id) return;
    setIsLoading(true);
    try {
      const params = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const result = await fetchGLAccountTransactions(authAxios, account.id, params);
      setData(result);
    } catch (err) {
      showFormMessage && showFormMessage('error', err.response?.data?.detail || 'Failed to load account transactions.');
    } finally {
      setIsLoading(false);
    }
  }, [account, authAxios, dateFrom, dateTo, showFormMessage]);

  useEffect(() => { load(); }, [load]);

  const transactions = data?.transactions || [];
  const { pageItems: pagedTransactions, paginationProps } = usePagination(transactions);
  const isTrustAccount = data?.account?.code === '1400';

  if (!account) return null;

  return (
    <div className="fixed inset-0 overflow-hidden z-50">
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose}></div>
        <section className="absolute inset-y-0 right-0 pl-10 max-w-full flex">
          <div className="w-screen max-w-[75vw]">
            <div className="h-full flex flex-col bg-white shadow-xl">
              <div className="px-4 py-6 sm:px-6 bg-white border-b">
                <div className="flex items-start justify-between">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">
                      {account.code} – {account.name}
                    </h2>
                    <p className="mt-1 text-sm text-gray-600">GL transactions behind this account's balance</p>
                  </div>
                  <div className="ml-3 h-7 flex items-center">
                    <button onClick={onClose} className="rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500">
                      <span className="sr-only">Close panel</span>
                      ×
                    </button>
                  </div>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-6">
                {isLoading ? (
                  <div className="flex justify-center items-center h-64">
                    <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-500"></div>
                  </div>
                ) : (
                  <>
                    {isTrustAccount && data && (
                      <div className={`mb-4 rounded-lg border p-4 ${data.is_variance_flagged ? 'border-red-300 bg-red-50' : 'border-emerald-200 bg-emerald-50'}`}>
                        <p className={`text-sm font-semibold ${data.is_variance_flagged ? 'text-red-800' : 'text-emerald-800'}`}>
                          Trust Bank (1400) shows two figures
                        </p>
                        <p className="text-xs text-gray-600 mt-0.5">
                          This account's balance on the Chart of Accounts screen is the live sum of client trust
                          ledgers, not this account's own GL journal-line total — trust transactions maintain their
                          own ledger. The two are expected to match; when they don't, that's a real reconciling item.
                        </p>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-3 text-sm">
                          <div className="bg-white p-3 rounded border">
                            <p className="text-xs text-gray-500 uppercase">Displayed (Trust Ledgers)</p>
                            <p className="text-lg font-bold">{formatCurrency(data.displayed_balance)}</p>
                          </div>
                          <div className="bg-white p-3 rounded border">
                            <p className="text-xs text-gray-500 uppercase">GL Journal-Line Total</p>
                            <p className="text-lg font-bold">{formatCurrency(data.gl_line_derived_balance)}</p>
                          </div>
                          <div className="bg-white p-3 rounded border">
                            <p className="text-xs text-gray-500 uppercase">Variance</p>
                            <p className={`text-lg font-bold ${data.is_variance_flagged ? 'text-red-700' : 'text-emerald-700'}`}>
                              {formatCurrency(data.trust_variance)}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="flex flex-wrap items-end gap-3 mb-4">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">From</label>
                        <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
                          className="border rounded px-2 py-1.5 text-sm" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">To</label>
                        <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
                          className="border rounded px-2 py-1.5 text-sm" />
                      </div>
                      {(dateFrom || dateTo) && (
                        <button onClick={() => { setDateFrom(''); setDateTo(''); }}
                          className="text-xs text-blue-600 hover:underline">Clear dates</button>
                      )}
                      {!isTrustAccount && data && (
                        <div className="ml-auto text-sm text-gray-600">
                          Balance: <span className="font-semibold text-gray-900">{formatCurrency(data.gl_line_derived_balance)}</span>
                        </div>
                      )}
                    </div>

                    <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200 text-sm">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Date</th>
                              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Entry #</th>
                              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Description</th>
                              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Source</th>
                              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Contra Account</th>
                              <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Debit</th>
                              <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Credit</th>
                              <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Running Balance</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                            {pagedTransactions.map((t, i) => (
                              <tr key={`${t.journal_entry_id}-${i}`} className={t.is_reversed ? 'bg-red-50 hover:bg-red-50' : 'hover:bg-gray-50'}>
                                <td className="px-4 py-2 text-gray-700">{t.date}</td>
                                <td className="px-4 py-2 font-mono text-blue-600">{t.entry_number}</td>
                                <td className="px-4 py-2 text-gray-800">
                                  {t.is_reversed && <span className="mr-2 inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-600">Reversed</span>}
                                  {t.description}
                                  {t.is_flagged && <span className="ml-2 inline-flex px-1.5 py-0.5 rounded-full text-xs bg-orange-100 text-orange-700">⚑</span>}
                                </td>
                                <td className="px-4 py-2 text-gray-500">{(t.source_type_display || t.source_type || '').toString()}</td>
                                <td className="px-4 py-2 text-gray-700">
                                  {(t.contra_accounts || []).map((c, ci) => (
                                    <div key={ci} className="whitespace-normal break-words">{c.code} - {c.name}</div>
                                  ))}
                                </td>
                                <td className="px-4 py-2 text-right">{parseFloat(t.debit) > 0 ? formatCurrency(t.debit) : ''}</td>
                                <td className="px-4 py-2 text-right">{parseFloat(t.credit) > 0 ? formatCurrency(t.credit) : ''}</td>
                                <td className="px-4 py-2 text-right font-medium">{formatCurrency(t.running_balance)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      <PaginationBar {...paginationProps} emptyLabel="No transactions posted to this account for the selected range." />
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
